<div class="grading-section form-section">
	<div class="form-group grading-form-group" [class.has-error]="shouldDisplayPointsPossibleError()">
		<mat-form-field appearance="outline" class="grading-select-field">
			<mat-label translate="activity-editor-new_grading-and-eval"></mat-label>
			<mat-select
				id="grading-type-dropdown"
				data-cy="grading-type-dropdown"
				[formControl]="gradingTypeControl"
				required>
				<mat-select-trigger>
					<span class="option-title">{{ getSelectedGradingTitle() }}</span>
				</mat-select-trigger>
				@for (option of gradingOptions; track option.key) {
					<mat-option [value]="option.key" [disabled]="isGradingOptionDisabled(option)" [attr.data-cy]="'rd-item-' + option.key.toLowerCase()">
						<div class="grading-option">
							<div class="option-title">
								{{ option.config.title | translate }}
							</div>
							<div class="option-description">
								{{ getGradingOptionDescriptionKey(option) | translate }}
							</div>
						</div>
					</mat-option>
				}
			</mat-select>
			<mat-icon matIconSuffix class="ficon-app-chevron-down"></mat-icon>
		</mat-form-field>

		@if (shouldDisplayPointsPossible() && pointsControl) {
			<mat-form-field
				appearance="outline"
				class="points-field"
				[class.readonly]="isPointsPossibleDisabled()">
				<mat-label translate="activity-editor-new_points-possible"></mat-label>
				<input
					matInput
					id="activity-editor-points-possible-input"
					type="number"
					min="0"
					step="0.01"
					data-cy="points-possible-value"
					[formControl]="pointsControl"
					[readonly]="isPointsPossibleDisabled()"
					[matTooltip]="getPointsDisabledTooltip()"
					[matTooltipDisabled]="!isPointsPossibleDisabled()"
					aria-describedby="points-error"
					(blur)="normalizePointsPossible()">
			</mat-form-field>
		}
	</div>
	@if (shouldDisplayPointsPossibleError()) {
		<mat-error id="points-error" translate="activity-editor-new_points-required"></mat-error>
	}

	@if (selectedRubric) {
		<ngx-panel>
			<panel-heading>
				<div class="title ellipsis">
					@if (isLoadingRubric) {
						<span class="ficon-spinner ficon-spin" aria-hidden="true"></span>
					} @else {
						<span translate="rubric_scoring_title"></span>
					}
				</div>

				<ngx-toolbar>
					<ngx-toolbar-button
						[disabled]="isLoadingRubric"
						[action]="toolbarActions.PREVIEW"
						(clicked)="handlePreview()">
					</ngx-toolbar-button>
					<ngx-toolbar-button
						[disabled]="isLoadingRubric"
						[action]="toolbarActions.EDIT"
						(clicked)="handleEdit()">
					</ngx-toolbar-button>
					<ngx-toolbar-button
						[disabled]="isLoadingRubric"
						[action]="toolbarActions.REMOVE"
						(clicked)="handleRemove()">
					</ngx-toolbar-button>
				</ngx-toolbar>
			</panel-heading>

			<panel-body>
				<span class="rubric-name">{{ selectedRubric.title || ('common_untitled' | translate) }}</span>

				<!-- Allow Reviewers Access -->
				<section class="reviewers-rubrics-access">
					<div class="reviewers-access">
						<mat-checkbox
							color="primary"
							[formControl]="reviewersAccessControl"
							data-cy="reviewers-rubric-access">
							<span translate="activity-editor_allow-reviewer-access"></span>
						</mat-checkbox>
						<button
							type="button"
							class="ficon-app-help-circle-outline icon-btn"
							[goCdkPopover]="'activity-editor-new_reviewers-rubrics-access-help' | translate"
							[attr.aria-label]="'activity-editor-new_reviewers-rubrics-access-help_aria-label' | translate">
						</button>
					</div>
				</section>

				<!-- Enable AI auto-grading -->
				@if (shouldShowAutoGrading()) {
					<section class="ai-auto-grading">
						<div class="ai-auto-grading-checkbox-wrapper">
							<mat-checkbox
								class="auto-grading-checkbox"
								color="primary"
								[formControl]="autoGradingControl">
								<span translate="activity-editor_allow-ai-auto-grading"></span>
							</mat-checkbox>
							<button
								type="button"
								class="ficon-app-help-circle-outline icon-btn"
								[goCdkPopover]="'activity-editor_allow-ai-auto-grading-tooltip' | translate"
								[attr.aria-label]="'activity-editor_allow-ai-auto-grading' | translate">
							</button>
						</div>

						@if (autoGradingControl.value === true) {
							<div class="activity-editor_auto_grading-settings-wrapper">
								<h5 translate="activity-editor_auto_grading-settings"></h5>
								<div class="auto-grading-setting">
									<mat-checkbox
										color="primary"
										class="auto-grading-use-ai-score"
										[formControl]="autoGradingUseAIScoreControl">
										<span translate="activity-editor_auto_grading-use-ai-score"></span>
									</mat-checkbox>
									<button
										type="button"
										class="ficon-app-help-circle-outline icon-btn"
										[goCdkPopover]="'activity-editor_auto_grading-use-ai-score-tooltip' | translate"
										[attr.aria-label]="'activity-editor_auto_grading-use-ai-score' | translate">
									</button>
								</div>
								<div class="auto-grading-setting">
									<mat-checkbox
										color="primary"
										class="auto-grading-allow-override"
										[formControl]="autoGradingAllowReviewerOverrideControl">
										<span translate="activity-editor_auto_grading-allow-override-score"></span>
									</mat-checkbox>
								</div>
								<div class="auto-grading-setting">
									<mat-checkbox
										color="primary"
										class="auto-grading-auto-post-ai-rubric"
										[formControl]="autoGradingAutoPostAIRubricControl">
										<span translate="activity-editor_auto_grading-auto-post-ai-graded"></span>
									</mat-checkbox>
									<button
										type="button"
										class="ficon-app-help-circle-outline icon-btn"
										[goCdkPopover]="'activity-editor_auto_grading-auto-post-ai-graded-tooltip' | translate"
										[attr.aria-label]="'activity-editor_auto_grading-auto-post-ai-graded' | translate">
									</button>
								</div>
								<div class="ai-context-setting" [class.has-ai-context]="hasAiContext()">
									<button
										type="button"
										class="btn-none add-ai-context-btn"
										(click)="openAiContextDialog()">
										@if (hasAiContext()) {
											<span class="add-ai-context-icon ficon-app-edit" aria-hidden="true"></span>
											<span class="add-ai-context-label" translate="activity-editor_ai-context-edit-action"></span>
										} @else {
											<span class="add-ai-context-icon" aria-hidden="true">+</span>
											<span class="add-ai-context-label" translate="activity-editor_ai-context-action"></span>
										}
									</button>
									<button
										type="button"
										class="ficon-app-help-circle-outline icon-btn add-ai-context-help"
										[goCdkPopover]="'activity-editor_ai-context-help' | translate"
										[attr.aria-label]="'activity-editor_ai-context-help_aria-label' | translate">
									</button>
									@if (hasAiContext()) {
										<button
											type="button"
											class="ficon-app-x icon-btn clear-ai-context-btn"
											(click)="clearAiContext()"
											[attr.aria-label]="'activity-editor_ai-context-clear_aria-label' | translate">
										</button>
									}
									@if (getAiContextPreview(); as aiContextPreview) {
										<p class="ai-context-preview">{{ aiContextPreview }}</p>
									}
								</div>
							</div>
						}
					</section>
				}

			</panel-body>
		</ngx-panel>
	}
</div>
